import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import deepmerge from 'deepmerge';
import { SortEvent } from 'primeng/api';
import { map, Observable, startWith } from 'rxjs';

import { StackingPlanService } from '../../../stacking-plan.service';
import { IdentifiedIssue, PendingIssuesFormGroup, PENDING_ISSUES_HEADERS } from './pending-issues.model';
import { SuiteType } from '../../floor-container/floor/item/item.model';
import { categorizeIssues } from './pending-issues.functions';
import { SortService, DataType } from '@miq/utils';

@UntilDestroy()
@Component({
  selector: 'sp-pending-issues',
  templateUrl: './pending-issues.component.html',
  styleUrls: ['./pending-issues.component.scss'],
  standalone: false,
})
export class PendingIssuesComponent implements OnInit, OnDestroy {
  pendingIssuesFG = new FormGroup({
    unknown: new FormControl(false),
    expired: new FormControl(false),
  });
  issuesTableHeader = PENDING_ISSUES_HEADERS;
  pendingIssuesData$: Observable<IdentifiedIssue[]>;
  endIndex = 10;
  rowsOptions = [5, 10, 15];
  defaultRows = 10;

  constructor(
    @Optional() private router: Router,
    @Inject(MAT_DIALOG_DATA) private pendingIssuesOriginalData: IdentifiedIssue[],
    private dialogRef: MatDialogRef<PendingIssuesComponent>,
    private stackingPlanService: StackingPlanService,
    private sortService: SortService
  ) {}

  ngOnInit() {
    this.pendingIssuesData$ = this.generatePendingIssuesList();
  }

  ngOnDestroy() {
    /* untilDestroyed requires it */
  }

  generatePendingIssuesList(): Observable<IdentifiedIssue[]> {
    const initialValue = this.pendingIssuesFG.value;
    return this.pendingIssuesFG.valueChanges.pipe(
      untilDestroyed(this),
      startWith(initialValue),
      map((pendingIssuesForm: PendingIssuesFormGroup) => {
        const { unknown, expired } = pendingIssuesForm;
        const categorizedIssues = categorizeIssues(this.pendingIssuesOriginalData);
        if (unknown && !expired) {
          return categorizedIssues.unknown;
        }
        if (!unknown && expired) {
          return categorizedIssues.expired;
        }
        return deepmerge([], this.pendingIssuesOriginalData);
      })
    );
  }

  /** Navigate user to appropriate record page */
  navigateToRecords(issue: IdentifiedIssue): void {
    if (this.router && issue.recordType !== SuiteType.Unknown) {
      this.dialogRef.close();
      const url = this.stackingPlanService.getNavigationURL(issue.recordType, issue.id);
      this.router.navigate([url]);
    }
  }

  sortPendingIssues(event: SortEvent): void {
    const { data, field, order } = event;
    data.sort((dataA, dataB) => {
      dataA = typeof dataA[field] === DataType.Object ? dataA[field].value : dataA[field];
      dataB = typeof dataB[field] === DataType.Object ? dataB[field].value : dataB[field];
      return this.sortService.singleSort(dataA, dataB, order, 'N/A');
    });
  }
}
